/**
 * Research Engine — Research Brief schema (data-driven field + merge spec).
 *
 * The brief contract expressed as DATA: `RESEARCH_FIELDS` lists every field and
 * HOW it merges when knowledge sources are composed. The merge engine iterates
 * this table, so there is no per-field logic and adding a field is one line.
 *
 * The brief is design intelligence only — no pixels, no editor/compiler/OpenAI
 * concepts. Genome/pattern recommendations are references into Phase 3.
 */

export const RESEARCH_SCHEMA_ID = 'research/1';
export const RESEARCH_VERSION = 1;

/** How a field combines across composed knowledge fragments. */
export const MERGE = Object.freeze({
  OVERRIDE: 'override',     // most-specific non-empty value wins (prose)
  UNION: 'union',           // deduped list, most-specific first (tags/directions)
  GENOME_REF: 'genomeRef',  // union, validated against the genome registry
  PATTERN_REF: 'patternRef',// union, validated against the library-pattern vocabulary
});

/**
 * @typedef {Object} ResearchFieldSpec
 * @property {string} key
 * @property {string} merge   One of MERGE.
 */

/** Every field of a Research Brief, in canonical order. */
export const RESEARCH_FIELDS = Object.freeze([
  { key: 'marketUnderstanding', merge: MERGE.OVERRIDE },
  { key: 'industryUnderstanding', merge: MERGE.OVERRIDE },
  { key: 'audienceUnderstanding', merge: MERGE.OVERRIDE },
  { key: 'competitorLandscape', merge: MERGE.OVERRIDE },
  { key: 'brandPositioning', merge: MERGE.UNION },
  { key: 'visualTrends', merge: MERGE.UNION },
  { key: 'marketingPsychology', merge: MERGE.UNION },
  { key: 'colorPsychology', merge: MERGE.UNION },
  { key: 'typographyDirection', merge: MERGE.UNION },
  { key: 'compositionDirection', merge: MERGE.UNION },
  { key: 'photographyDirection', merge: MERGE.UNION },
  { key: 'illustrationDirection', merge: MERGE.UNION },
  { key: 'iconographyDirection', merge: MERGE.UNION },
  { key: 'ctaBehaviour', merge: MERGE.UNION },
  { key: 'premiumCues', merge: MERGE.UNION },
  { key: 'thingsToAvoid', merge: MERGE.UNION },
  { key: 'recommendedGenomes', merge: MERGE.GENOME_REF },
  { key: 'recommendedPatterns', merge: MERGE.PATTERN_REF },
]);

/** Keys allowed on a knowledge fragment (brief fields + source metadata). */
export const FRAGMENT_META_KEYS = Object.freeze(['asOf', 'confidence', 'note']);

const FIELD_KEYS = RESEARCH_FIELDS.map((f) => f.key);
export const ALLOWED_FRAGMENT_KEYS = Object.freeze([...FIELD_KEYS, ...FRAGMENT_META_KEYS]);

export const PROSE_FIELDS = Object.freeze(RESEARCH_FIELDS.filter((f) => f.merge === MERGE.OVERRIDE).map((f) => f.key));
export const LIST_FIELDS = Object.freeze(RESEARCH_FIELDS.filter((f) => f.merge !== MERGE.OVERRIDE).map((f) => f.key));

/** The dimensions a research request may specify, in composition precedence. */
export const SOURCE_ORDER = Object.freeze([
  'base', 'industry', 'category', 'businessSize', 'premiumLevel',
  'audience', 'purpose', 'region', 'culture', 'season', 'event',
]);

/** Requestable dimensions (everything in SOURCE_ORDER except the implicit base). */
export const REQUEST_DIMENSIONS = Object.freeze(SOURCE_ORDER.filter((d) => d !== 'base'));
